/**
 * Disponibilidade e reservas (Task 11, brief 3.2 / D-11.5).
 *
 * Uma linha por produto com a conta aberta: estoque vendável, reservas ativas
 * e a disponibilidade consolidada que sai delas. É a MESMA conta que o KPI de
 * estoque crítico do dashboard usa (`consolidatedAvailability` de
 * `packages/catalog`, com o mesmo corte `CRITICAL_STOCK_THRESHOLD`) — card e
 * tela não podem divergir.
 *
 * "Sem oferta vendável" e "disponibilidade 0" são estados diferentes e
 * aparecem diferentes: o primeiro é "não há o que vender", o segundo é
 * "esgotado/reservado".
 *
 * Migração para `.tsx` (Cluster B — Catálogo & Estoque, redesign por perfil):
 * mesmo markup/contrato de antes, só a sintaxe `createElement` → JSX. Import
 * de `React` explícito — ver nota equivalente em `layout/AppShell.tsx`.
 */
import React, { type ReactElement } from "react";
import { Link } from "react-router-dom";
import { Ban, CheckCircle2, type LucideIcon, TriangleAlert } from "lucide-react";
import { RecordTable } from "../components/RecordTable.js";
import { CatalogBoundary } from "../components/CatalogBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { availabilityOfProduct, CRITICAL_STOCK_THRESHOLD } from "../catalog/availability.js";
import type { CatalogDataset } from "../catalog/dataset.js";
import { formatCount } from "../format.js";
import type { CatalogViewProps } from "../catalog/view-props.js";
import type { PlatformCatalogProduct } from "../api/types.js";

type AvailabilitySituation = "disponivel" | "critico" | "indisponivel";

const SITUATION_META: Record<AvailabilitySituation, { tone: string; label: string; Icon: LucideIcon }> = {
  disponivel: { tone: "border-success text-success", label: "disponível", Icon: CheckCircle2 },
  critico: { tone: "border-warning text-warning", label: "estoque crítico", Icon: TriangleAlert },
  indisponivel: { tone: "border-danger text-danger", label: "indisponível (sem oferta vendável)", Icon: Ban }
};

/**
 * Selo de situação da disponibilidade: ícone + texto, nunca só cor (régua §2).
 * Disponível (success/check), estoque crítico (warning/alerta), indisponível
 * sem oferta (danger/bloqueio). O texto EXATO é preservado — os testes batem
 * nele ("indisponível (sem oferta vendável)").
 */
function AvailabilitySituationBadge({ kind }: { kind: AvailabilitySituation }): ReactElement {
  const meta = SITUATION_META[kind];
  return (
    <span className={"inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold " + meta.tone}>
      <meta.Icon className="h-3.5 w-3.5 shrink-0" aria-hidden focusable={false} />
      <span>{meta.label}</span>
    </span>
  );
}

export function DisponibilidadeView({ dataset }: CatalogViewProps): ReactElement {
  if (dataset.sources.platformCatalog === "awaiting_contract") {
    return (
      <div className="page page--disponibilidade flex flex-col gap-6" data-page="disponibilidade">
        <header className="drilldown-header flex flex-col gap-1">
          <h2 className="text-2xl font-extrabold text-ink">Disponibilidade e reservas</h2>
        </header>
        <StatusPanel
          kind="awaiting_source"
          title="Fonte de estoque/reservas não publicada neste ambiente"
          detail="A rota /api/catalog/platform/products traz estoque vendável e reservas. Sem ela não exibimos disponibilidade — um zero aqui seria lido como 'esgotado'."
        />
      </div>
    );
  }
  const breakdowns = new Map(dataset.products.map((product) => [product.productId, availabilityOfProduct(product)]));
  const critical = dataset.products.filter((product) => breakdowns.get(product.productId)?.critical);
  return (
    <div className="page page--disponibilidade flex flex-col gap-6" data-page="disponibilidade">
      <header className="drilldown-header flex flex-col gap-1">
        <h2 className="text-2xl font-extrabold text-ink">Disponibilidade e reservas</h2>
        <p className="drilldown-header__origin text-sm text-muted" data-critical-count={critical.length}>
          {`${dataset.products.length} produto(s); ${critical.length} com disponibilidade consolidada <= ${CRITICAL_STOCK_THRESHOLD}.`}
        </p>
        <p className="page__note text-sm text-muted">
          Disponibilidade = estoque da oferta vendável vigente − reservas ativas (a mesma regra da função SQL consolidated_availability,
          migration 0019). Estoque bruto do operacional nunca é exibido como disponível.
        </p>
      </header>
      <RecordTable<PlatformCatalogProduct>
        caption="Estoque vendável, reservas ativas e disponibilidade consolidada"
        rows={dataset.products}
        idOf={(product) => product.productId}
        emptyMessage="Nenhum produto no catálogo."
        // Fase 7 (Plano de Ataque R8): rollout mecânico do primitivo da Fase 6.
        animateRows
        columns={[
          {
            key: "sku",
            label: "SKU",
            render: (product) => (
              <Link to={`/catalogo/${product.productId}`} className="font-semibold text-brand-text hover:underline">
                {product.operational.sku}
              </Link>
            )
          },
          {
            key: "name",
            label: "Produto",
            render: (product) => product.commercial.commercialName ?? product.name
          },
          {
            key: "sellable",
            label: "Estoque vendável",
            render: (product) => {
              const breakdown = breakdowns.get(product.productId);
              return breakdown?.sellableStock === null || breakdown === undefined ? "sem oferta vendável" : formatCount(breakdown.sellableStock);
            }
          },
          {
            key: "reserved",
            label: "Reservas ativas",
            render: (product) => formatCount(breakdowns.get(product.productId)?.activeReservations ?? 0)
          },
          {
            key: "consolidated",
            label: "Disponibilidade consolidada",
            render: (product) => {
              const breakdown = breakdowns.get(product.productId);
              if (!breakdown) return "—";
              return (
                <span data-availability={breakdown.consolidated} data-critical={breakdown.critical ? "true" : "false"}>
                  {formatCount(breakdown.consolidated)}
                </span>
              );
            }
          },
          {
            key: "status",
            label: "Situação",
            render: (product) => {
              const breakdown = breakdowns.get(product.productId);
              if (!breakdown) return "—";
              if (breakdown.sellableStock === null) return <AvailabilitySituationBadge kind="indisponivel" />;
              if (breakdown.critical) return <AvailabilitySituationBadge kind="critico" />;
              return <AvailabilitySituationBadge kind="disponivel" />;
            }
          }
        ]}
      />
    </div>
  );
}

export function Disponibilidade(): ReactElement {
  return <CatalogBoundary>{(dataset: CatalogDataset) => <DisponibilidadeView dataset={dataset} />}</CatalogBoundary>;
}
